import type { Routes } from '@angular/router';
import { DecisionAssistantComponent } from '../decision-card/decision-assistant.component';
import type { GuidedDecisionAssignmentConfig } from '../decision-card/decision-assistant.model';

const DDW_VIS_ADOPT_OPTION =
  'adopt-reviewed-presentation — Adopt the accepted reduced trusted-renderer presentation (recommended)';
const DDW_VIS_RETURN_OPTION =
  'return-for-recomposition — Return the presentation for a separately reviewed correction';
const DDW_INTEGRATION_HEAD = 'bb374313d9c588a525c00ef37cdabf99d675cff1';
const DDW_INTEGRATION_APPROVE_OPTION = `APPROVE_MERGE — Approve manual merge of Wiki PR #546 only at head ${DDW_INTEGRATION_HEAD} after the bound validation remains green (recommended)`;
const DDW_INTEGRATION_REQUEST_CHANGES_OPTION =
  'REQUEST_CHANGES — Do not merge this head; close this attempt and require a corrected PR/head plus a new approval card';
const DDW_INTEGRATION_DECLINE_OPTION = 'DECLINE — Close this publication attempt without merging';

/** Replaced with an empty route list by the Production Angular build. */
export const decisionDemoRoutes: Routes = [
  {
    path: 'demo/decision-card',
    pathMatch: 'full',
    redirectTo: 'agentic/manual-ddw',
  },
  {
    path: 'agentic/manual-ddw',
    title: 'Manual DDW assignment persistence proof',
    loadComponent: () =>
      import('./decision-demo.component').then((module) => module.DecisionDemoComponent),
  },
  {
    path: 'agentic/ddw-vis-review',
    title: 'DDW-VIS human review candidate',
    loadComponent: () =>
      import('./ddw-vis-review.component').then((module) => module.DdwVisReviewComponent),
  },
];

/** Exact login return targets owned by the Development-only route list. */
export const decisionDemoReturnUrls: readonly string[] = decisionDemoRoutes.flatMap((route) =>
  route.path && !route.redirectTo ? [`/${route.path}`] : [],
);

/** Exact owner presentations allowed to use Development-only experimental interactions. */
export const guidedDecisionAssignments: readonly GuidedDecisionAssignmentConfig[] = [
  {
    assignmentId: 'DA65e48938e7764662e2bb48c9df33939d',
    sourceRequirementId: 'DDW-VIS',
    sourcePath: 'discovery/problems/agentic/delegated_discovery_work',
    sourceCommitSha: 'ca50aac6800becdc550b66eb7738edb0936da293',
    sourceContentHash: 'b7e261ba4f2aa76b6d5bf3f4609a835cae2abb436b03df8fc9f4f8a62657a000',
    workflowMode: 'manual_review',
    contentRevision: 1,
    contractHash: '412d4a2c82c516da3e1cee8f03e2436b25cb59c7e24bee167682e3a8e1c64d64',
    requiredQuestion: {
      id: 'presentation-adoption',
      answerType: 'choice',
      options: [DDW_VIS_ADOPT_OPTION, DDW_VIS_RETURN_OPTION],
    },
    optionStances: [
      { option: DDW_VIS_ADOPT_OPTION, stance: 'CONFIRMED' },
      { option: DDW_VIS_RETURN_OPTION, stance: 'AMEND_SCOPE' },
    ],
    customAnswerStance: 'AMEND_SCOPE',
    completionCopy:
      'This records the selected outcome only. Any downstream receipt or integration remains a separate authorized reconciliation.',
    assistant: {
      component: DecisionAssistantComponent,
      config: {
        assignmentId: 'DA65e48938e7764662e2bb48c9df33939d',
        expectedContentRevision: 1,
        expectedContractHash: '412d4a2c82c516da3e1cee8f03e2436b25cb59c7e24bee167682e3a8e1c64d64',
        expectedPresentationRevision: 2,
        expectedPresentationHash:
          '0dc5fff18d6f796b5f4760e89664f1c45e238614572f6a25acf8865b15ccfd14',
        defaultModelRoute: 'ollama/glm-5.3',
        models: [
          { route: 'ollama/glm-5.3', label: 'GLM 5.3' },
          { route: 'ollama/deepseek-v4-pro:0813', label: 'DeepSeek V4 Pro' },
        ],
      },
    },
  },
  {
    assignmentId: 'DA879eb498c19c461c70f7719c7c107366',
    sourceRequirementId: 'DDW-INTEGRATION-PR546-bb37431',
    sourcePath: 'discovery/problems/agentic/delegated_discovery_work',
    sourceCommitSha: DDW_INTEGRATION_HEAD,
    sourceContentHash: 'b7e261ba4f2aa76b6d5bf3f4609a835cae2abb436b03df8fc9f4f8a62657a000',
    workflowMode: 'manual_review',
    contentRevision: 1,
    contractHash: 'efb7a0530ff2a611c73df0a30e83fe1aa69bb41fbe3bdb2463aec7cbcaa07bb3',
    requiredQuestion: {
      id: 'integration-disposition',
      answerType: 'choice',
      options: [
        DDW_INTEGRATION_APPROVE_OPTION,
        DDW_INTEGRATION_REQUEST_CHANGES_OPTION,
        DDW_INTEGRATION_DECLINE_OPTION,
      ],
    },
    optionStances: [
      { option: DDW_INTEGRATION_APPROVE_OPTION, stance: 'CONFIRMED' },
      { option: DDW_INTEGRATION_REQUEST_CHANGES_OPTION, stance: 'AMEND_SCOPE' },
      { option: DDW_INTEGRATION_DECLINE_OPTION, stance: 'REJECT' },
    ],
    customAnswerStance: 'AMEND_SCOPE',
    completionCopy:
      'This records the human disposition only. It does not merge or modify PR #546; any GitHub action requires separate authorization and a fresh evidence check.',
    decisionReference: {
      kind: 'github_pull_request',
      label: 'Wiki PR #546',
      pullRequestNumber: 546,
      url: 'https://github.com/raidenhq/wiki/pull/546',
    },
    assistant: {
      component: DecisionAssistantComponent,
      config: {
        assignmentId: 'DA879eb498c19c461c70f7719c7c107366',
        expectedContentRevision: 1,
        expectedContractHash: 'efb7a0530ff2a611c73df0a30e83fe1aa69bb41fbe3bdb2463aec7cbcaa07bb3',
        expectedPresentationRevision: 1,
        expectedPresentationHash:
          '121b73e09e1efbe62ce08ff1f8a30a6f9df1c17f3e3b543eb34bed9f448ea3ce',
        defaultModelRoute: 'ollama/glm-5.3',
        models: [
          { route: 'ollama/glm-5.3', label: 'GLM 5.3' },
          { route: 'ollama/deepseek-v4-pro:0813', label: 'DeepSeek V4 Pro' },
        ],
      },
    },
  },
  {
    assignmentId: 'DA6dbb98f322110e9fbd86a7f46fe226f3',
    sourceRequirementId: 'AMA-SLACK-ALERT',
    sourcePath: 'discovery/problems/agentic/agentic_monitoring_and_alerts',
    sourceCommitSha: '7ac968719430a5ec1451ff18b85ed225e71f8779',
    sourceContentHash: '770e373a1b1397d539faca0d5c9bf5ea7638efe263aad62d453354ef4d0b26ba',
    workflowMode: 'manual_review',
    contentRevision: 1,
    contractHash: '873cd93e41444a9f2ec50c3a7a0336a5557b52c5a10df7ba574794bbecd7d014',
    requiredQuestion: {
      id: 'alert-format',
      answerType: 'choice',
      options: [
        'Remove redundant summary count line (proceed directly to Decreased/Increased breakdown) (recommended)',
        'Retain current summary count line',
      ],
    },
    optionStances: [
      {
        option:
          'Remove redundant summary count line (proceed directly to Decreased/Increased breakdown) (recommended)',
        stance: 'CONFIRMED',
      },
      { option: 'Retain current summary count line', stance: 'REJECT' },
    ],
    customAnswerStance: 'AMEND_SCOPE',
    completionCopy:
      'This records the decision to refine the weekly Slack consumption alert format.',
  },
];

export const guidedDecisionAssignmentIds: readonly string[] = guidedDecisionAssignments.map(
  (assignment) => assignment.assignmentId,
);
